/**
 * MessageDispatcher - Type-safe command dispatch without protocol coupling.
 *
 * Maps semantic command names to protocol messages, hiding the underlying
 * protocol structure from the rest of the application.
 *
 * Benefits:
 * - Type-safe command payloads
 * - Protocol changes isolated to this file
 * - Single place to add logging, metrics, etc.
 * - Clear API surface for all commands
 */

import { logger } from '../log';
import {
  cmd,
  CmdType,
  DeleteCmdType,
  DeleteMoreCmdType,
  UploadCmdType,
  ProfileCmdType,
  LocalVersionCmdType,
  VersionCmdType,
  CustomHostnameCmdType,
  type ProfileCreateReq,
  type ProfileUpdateReq,
  type ProfileMapApplicationReq,
  type ProfileUnmapApplicationReq,
  type ProfileUpdateMappingReq,
  type ProfileBatchMapApplicationsReq,
  type LocalVersionCreateReq,
  type LocalVersionUpdateReq,
  type VersionUpdateReq,
  type CustomHostnameCreateReq,
  type CustomHostnameDeleteReq,
  type CustomHostnameRefreshReq,
} from "@interlucent/organization-protocol";

// =============================================================================
// COMMAND DEFINITIONS
// =============================================================================

/**
 * All available commands with their payload types.
 * This is the single source of truth for the command API.
 */
export type CommandDefinitions = {
  // Project commands
  'project.create': { name: string; slugHint?: string };
  'project.update': { id: string; name?: string; description?: string | null; active?: boolean; rowVersion: number };
  'project.delete': { projectId: string };
  'project.jwks.generate': { projectId: string; description: string };
  'project.jwks.list': { projectId: string };
  'project.jwks.delete': { projectId: string; kid: string };
  'project.anonymous_jwks.generate': {
    projectId: string;
    name: string;
    description: string;
    applicationId?: string;
    versionId?: string;
    reference?: string;
    swiftJobRequest?: boolean;
    expiresInSeconds?: number;
  };

  // Application commands
  'application.create': { projectId: string; name: string; slugHint?: string };
  'application.delete': { appId: string };
  'application.setDefaultVersion': { appId: string; versionId: string };
  'application.update': { id: string; name?: string; thumbnailDataUri?: string | null; rowVersion: number };
  'application.move': { appId: string; targetProjectId: string; rowVersion?: number };

  // Version commands
  'version.delete': { versionId: string };
  'version.update': VersionUpdateReq;

  // Profile commands
  'profile.create': ProfileCreateReq;
  'profile.createLegacy': { projectId: string; name: string; slugHint: string };
  'profile.update': ProfileUpdateReq;
  'profile.delete': { profileId: string };
  'profile.activate': { profileId: string };
  'profile.deactivate': { profileId: string };
  'profile.forcePushConfig': { profileId: string };
  'profile.generateWorkerToken': { profileId: string; expiresInSeconds?: number };

  // Mapping commands
  'mapping.map': ProfileMapApplicationReq;
  'mapping.unmap': ProfileUnmapApplicationReq;
  'mapping.update': ProfileUpdateMappingReq;
  'mapping.batchMap': ProfileBatchMapApplicationsReq;
  // Upload commands
  'upload.abort': { uploadId: string; key: string };
  'upload.pause': { uploadId: string };
  'upload.resume': { uploadId: string };
  'upload.list': Record<string, never>;
  'upload.status': { uploadId: string };

  // Onboarding commands
  'onboarding.getProgress': Record<string, never>;
  'onboarding.getPlans': Record<string, never>;
  'onboarding.setOrgName': { orgName: string };
  'onboarding.selectPlan': { planId: string; orgId: string; orgName: string; returnUrl?: string };
  'onboarding.verifyPayment': { stripeSessionId: string };
  'onboarding.complete': { orgId: string; invitesSent: boolean };

  // Subscription commands
  'subscription.verify': { subscriptionId: string };

  // Preview share commands
  'preview.share.create': {
    projectId: string;
    appId?: string;
    versionId?: string;
    url?: string;
    expiresInMinutes?: number;
    expiresInSeconds?: number;
    passcode?: string;
  };
  'preview.share.list': { projectId?: string; appId?: string };
  'preview.share.revoke': { id: string };

  // Local version commands
  'localVersion.create': LocalVersionCreateReq;
  'localVersion.update': LocalVersionUpdateReq;
  'localVersion.delete': { id: string };
  'localVersion.getTokens': { id: string };
  'localVersion.cycleTokens': { id: string };

  // Custom hostname commands
  'customHostname.create': CustomHostnameCreateReq;
  'customHostname.delete': CustomHostnameDeleteReq;
  'customHostname.refresh': CustomHostnameRefreshReq;
};

export type CommandName = keyof CommandDefinitions;

// =============================================================================
// COMMAND TYPE MAPPING
// =============================================================================

/**
 * Maps semantic command names to protocol command types.
 */
const commandTypeMap: Record<CommandName, string> = {
  // Project
  'project.create': CmdType.ProjectCreate,
  'project.update': CmdType.ProjectUpdate,
  'project.delete': DeleteMoreCmdType.ProjectDelete,
  'project.jwks.generate': "cmd.project.jwks.generate",
  'project.jwks.list': "cmd.project.jwks.list",
  'project.jwks.delete': "cmd.project.jwks.delete",
  'project.anonymous_jwks.generate': "cmd.project.anonymous_jwks.generate",

  // Application
  'application.create': CmdType.ApplicationCreate,
  'application.delete': DeleteMoreCmdType.ApplicationDelete,
  'application.setDefaultVersion': CmdType.ApplicationSetDefaultVersion,
  'application.update': CmdType.ApplicationUpdate,
  'application.move': CmdType.ApplicationMove,

  // Version
  'version.delete': DeleteCmdType.VersionDelete,
  'version.update': VersionCmdType.Update,

  // Profile
  'profile.create': ProfileCmdType.ProfileCreate,
  'profile.createLegacy': "cmd.profile.create",
  'profile.update': ProfileCmdType.ProfileUpdate,
  'profile.delete': DeleteMoreCmdType.ProfileDelete,
  'profile.activate': ProfileCmdType.ProfileActivate,
  'profile.deactivate': ProfileCmdType.ProfileDeactivate,
  'profile.forcePushConfig': ProfileCmdType.ProfileForcePushConfig,
  'profile.generateWorkerToken': ProfileCmdType.ProfileGenerateWorkerToken,

  // Mapping
  'mapping.map': ProfileCmdType.ProfileMapApplication,
  'mapping.unmap': ProfileCmdType.ProfileUnmapApplication,
  'mapping.update': ProfileCmdType.ProfileUpdateMapping,
  'mapping.batchMap': ProfileCmdType.ProfileBatchMapApplications,
  // Upload
  'upload.abort': UploadCmdType.UploadAbort,
  'upload.pause': UploadCmdType.UploadPause,
  'upload.resume': UploadCmdType.UploadResume,
  'upload.list': UploadCmdType.UploadList,
  'upload.status': UploadCmdType.UploadStatus,

  // Onboarding
  'onboarding.getProgress': CmdType.OnboardingGetProgress,
  'onboarding.getPlans': CmdType.OnboardingGetPlans,
  'onboarding.setOrgName': CmdType.OnboardingSetOrgName,
  'onboarding.selectPlan': CmdType.OnboardingSelectPlan,
  'onboarding.verifyPayment': CmdType.OnboardingVerifyPayment,
  'onboarding.complete': CmdType.OnboardingComplete,

  // Subscription
  'subscription.verify': CmdType.SubscriptionVerify,

  // Preview share
  'preview.share.create': "cmd.preview.share.create",
  'preview.share.list': "cmd.preview.share.list",
  'preview.share.revoke': "cmd.preview.share.revoke",

  // Local version
  'localVersion.create': LocalVersionCmdType.Create,
  'localVersion.update': LocalVersionCmdType.Update,
  'localVersion.delete': LocalVersionCmdType.Delete,
  'localVersion.getTokens': LocalVersionCmdType.GetTokens,
  'localVersion.cycleTokens': LocalVersionCmdType.CycleTokens,

  // Custom hostname
  'customHostname.create': CustomHostnameCmdType.Create,
  'customHostname.delete': CustomHostnameCmdType.Delete,
  'customHostname.refresh': CustomHostnameCmdType.Refresh,
};

// =============================================================================
// PAYLOAD TRANSFORMERS
// =============================================================================

/**
 * Transform semantic payloads to protocol payloads where needed.
 * Most commands pass through unchanged; this handles special cases.
 */
function transformPayload<K extends CommandName>(
  command: K,
  payload: CommandDefinitions[K]
): object {
  switch (command) {
    case 'project.delete':
      return payload; // { projectId } stays as-is
    case 'version.delete':
      return { versionId: (payload as CommandDefinitions['version.delete']).versionId };
    case 'profile.delete':
      return { profileId: (payload as CommandDefinitions['profile.delete']).profileId };
    case 'profile.activate':
    case 'profile.deactivate':
    case 'profile.forcePushConfig':
      return { id: (payload as CommandDefinitions['profile.activate']).profileId };
    case 'profile.generateWorkerToken': {
      const p = payload as CommandDefinitions['profile.generateWorkerToken'];
      return { id: p.profileId, ...(p.expiresInSeconds != null && { expiresInSeconds: p.expiresInSeconds }) };
    }
    case 'localVersion.delete':
    case 'localVersion.getTokens':
    case 'localVersion.cycleTokens':
      return { id: (payload as { id: string }).id };
    default:
      return payload;
  }
}

// =============================================================================
// DISPATCHER CLASS
// =============================================================================

const QUEUE_TTL_MS = 10_000;

export class MessageDispatcher {
  private ws: WebSocket | null = null;
  private snapshotReady = false;
  private readyWaiters: Array<() => void> = [];
  private queue: Array<{ message: string; expiresAt: number }> = [];

  /**
   * Set the WebSocket connection to use for dispatching.
   * Resets snapshot readiness — a new connection means a new snapshot is incoming.
   */
  setWebSocket(ws: WebSocket | null): void {
    this.ws = ws;
    this.snapshotReady = false;
  }

  /**
   * Signal that the initial snapshot has been fully received.
   * Flushes any queued commands and resolves pending waitForReady() calls.
   */
  notifyReady(): void {
    this.snapshotReady = true;
    this.tryFlush();
  }

  /**
   * Get the current WebSocket connection.
   */
  getWebSocket(): WebSocket | null {
    return this.ws;
  }

  /**
   * Dispatch a command with type-safe payload.
   * If the connection isn't ready (ws not open or snapshot pending),
   * the message is queued and sent automatically when ready.
   */
  dispatch<K extends CommandName>(
    command: K,
    payload: CommandDefinitions[K],
    corr?: string
  ): void {
    const log = logger('dispatcher');
    const type = commandTypeMap[command];
    const transformedPayload = transformPayload(command, payload);
    const message = cmd(type, transformedPayload, corr);
    const serialized = JSON.stringify(message);

    if (this.isReady()) {
      log.debug(`Sending ${command}`, message);
      this.ws!.send(serialized);
    } else {
      log.info(`Queued ${command} (waiting for connection + snapshot)`);
      this.queue.push({ message: serialized, expiresAt: Date.now() + QUEUE_TTL_MS });
    }
  }

  /**
   * Returns a promise that resolves when the dispatcher is ready
   * (WebSocket open AND snapshot complete).
   */
  waitForReady(timeoutMs = 10_000): Promise<void> {
    if (this.isReady()) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        const idx = this.readyWaiters.indexOf(wrappedResolve);
        if (idx >= 0) this.readyWaiters.splice(idx, 1);
        reject(new Error('Timed out waiting for WebSocket connection'));
      }, timeoutMs);

      const wrappedResolve = () => {
        clearTimeout(timer);
        resolve();
      };

      this.readyWaiters.push(wrappedResolve);
    });
  }

  /**
   * Check if the dispatcher is ready to send commands.
   * Requires both an open WebSocket AND a completed snapshot.
   */
  isReady(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN && this.snapshotReady;
  }

  /**
   * Flush queued messages and resolve pending waiters.
   * Only runs when isReady() is true.
   */
  private tryFlush(): void {
    if (!this.isReady()) return;

    const log = logger('dispatcher');
    const now = Date.now();

    // Flush queued messages
    while (this.queue.length > 0) {
      const item = this.queue.shift()!;
      if (item.expiresAt > now) {
        log.debug('Flushing queued message');
        this.ws!.send(item.message);
      } else {
        log.info('Dropped expired queued message');
      }
    }

    // Resolve all pending waiters
    const waiters = this.readyWaiters;
    this.readyWaiters = [];
    for (const resolve of waiters) {
      resolve();
    }
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

/**
 * Global dispatcher instance.
 * Use this for most cases; create new instances only for testing.
 */
export const dispatcher = new MessageDispatcher();

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Dispatch a command using the global dispatcher.
 * Shorthand for `dispatcher.dispatch(...)`.
 */
export function dispatch<K extends CommandName>(
  command: K,
  payload: CommandDefinitions[K],
  corr?: string
): void {
  dispatcher.dispatch(command, payload, corr);
}

/**
 * Set the WebSocket on the global dispatcher.
 */
export function setDispatcherWebSocket(ws: WebSocket | null): void {
  dispatcher.setWebSocket(ws);
}

/**
 * Signal that the initial snapshot is complete and commands can be sent.
 */
export function notifyDispatcherReady(): void {
  dispatcher.notifyReady();
}

/**
 * Wait for the dispatcher to be ready (WebSocket open + snapshot complete).
 */
export function waitForReady(timeoutMs?: number): Promise<void> {
  return dispatcher.waitForReady(timeoutMs);
}
